/* What a mark's size stands for, resolved against the fields the view is actually offering.
 *
 * Two settings rather than one tagged string, because a field can be called anything: a workspace
 * with a numeric field named "References" would otherwise be indistinguishable from the reference
 * count, and the collision would be silent. `nodeSizeBy` says which kind of quantity, and
 * `sizeByFieldName` says which field when the answer is a field.
 */

import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';

export type Network3DNodeSizeBy = 'uniform' | 'references' | 'field';

export type Network3DNodeSizing =
  | { kind: 'uniform' }
  | { kind: 'references' }
  | { kind: 'field'; fieldName: string };

/**
 * Checked against the offered fields rather than merely for emptiness, for every reason
 * `toSizing` gives in the hierarchical treemap.
 *
 * These settings are persisted and belong to the reader rather than to a workspace, so they
 * outlive the field they name: the reader changes context, the field is deleted or renamed, the
 * traversal is edited until the field's component type leaves scope, or a viewpoint arrives
 * carrying a confidential field its reader cannot see - which is *in* the offered list rather than
 * absent from it, since the dropdown disables such an option rather than dropping it, so the flag
 * has to be read as well as the name.
 *
 * The fallback is the reference count rather than uniform, which is the one place this departs
 * from the treemap. A treemap sized by a vanished field draws a blank frame, so anything is better
 * than nothing; here the picture is legible either way, and falling back to the degree keeps the
 * channel carrying something rather than quietly switching it off.
 */
export const toNodeSizing = (
  nodeSizeBy: Network3DNodeSizeBy,
  sizeByFieldName: string,
  offeredFields: readonly Pick<
    FieldWithPermissions,
    'name' | 'isConfidential'
  >[]
): Network3DNodeSizing => {
  if (nodeSizeBy === 'uniform') return { kind: 'uniform' };
  if (nodeSizeBy === 'references') return { kind: 'references' };

  const isOffered = offeredFields.some(
    ({ name, isConfidential }) => name === sizeByFieldName && !isConfidential
  );
  return isOffered
    ? { kind: 'field', fieldName: sizeByFieldName }
    : { kind: 'references' };
};

/**
 * The weight a mark carries, from the resolved sizing.
 *
 * `null` for a component with no value for the chosen field, which is what `NetworkNode.weight`
 * documents as "no quantity" - drawn smallest and left out of the normalisation. Not a small
 * number, and not zero:
 *
 * - a small number is a *measurement*, and a cost field lives on one component type, so most of a
 *   mixed viewpoint would be "measured as nearly nothing". That drags the typical mark below every
 *   real value and pins all of them to the cap, which draws as a screen of identical spheres.
 * - zero means "not a mark" and would hide the component, so a measurement of zero is reported as
 *   no measure rather than passed through. Which is where this parts company with
 *   every other size-by-field view in the product: a treemap or a bubble chart can let an
 *   unmeasured component vanish, because area *is* the datum there. A mark here is also a node
 *   with references attached and a position that means something, so hiding it leaves a reader
 *   looking at edges that terminate in mid-air.
 */
export const toNodeWeight = (
  sizing: Network3DNodeSizing,
  { degree, fieldValue }: { degree: number; fieldValue: number | null }
): number | null => {
  if (sizing.kind === 'uniform') return 1;
  if (sizing.kind === 'references') return 1 + degree;
  /* A measurement of zero is a measurement - "this costs nothing" - but zero is the one value the
     renderer reads as "not a mark", so passing it through made a component with a cost of 0
     disappear from the picture entirely. Reported as no measure instead, which draws it at the
     floor alongside the components that carry no value at all. Negatives go the same way for the
     same reason: there is no such thing as a negative area. */
  return fieldValue !== null && fieldValue <= 0 ? null : fieldValue;
};
